import React, { useEffect, useState } from "react";
import axios from "axios";
import { LicenseOrderForm } from "@/components/LicenseOrderForm";
import { OrderStatusPill, OrderHistory } from "@/components/LicenseOrdersAdmin";
import { LicensePurchase } from "@/components/LicensePurchase";
import { fmtLocal } from "@/lib/time";
import { API, useAuth, formatApiError } from "@/auth";
import { KeyRound, ShieldCheck, Timer, Loader2 } from "lucide-react";

export default function LicencesPage() {
  const { user, refreshMe } = useAuth();
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const activate = async (e) => {
    e.preventDefault(); setLoading(true); setMsg(null);
    try {
      await axios.post(`${API}/licenses/activate`, { key });
      await refreshMe();
      setMsg({ ok: true, text: "Licence activée avec succès." });
      setKey("");
    } catch (err) {
      setMsg({ ok: false, text: formatApiError(err) });
    } finally { setLoading(false); }
  };

  const expDays = user?.license_expires_at
    ? Math.max(0, Math.ceil((new Date(user.license_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="licences-page">
      <div className="card-lhs p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono-tech">Ma licence</div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Gestion des licences</h1>
            <p className="text-zinc-400 text-sm mt-2">
              Activez une clé pour débloquer les fonctions professionnelles de LHSunlocker.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center w-full lg:w-auto">
            <StatMini icon={ShieldCheck} label="Statut" value={user?.license_status === "active" ? "ACTIVE" : "INACTIVE"} color={user?.license_status === "active" ? "text-emerald-400" : "text-red-400"} />
            <StatMini icon={KeyRound} label="Formule" value={user?.license_plan || "—"} />
            <StatMini icon={Timer} label="Jours restants" value={user?.license_status === "active" ? expDays : "—"} color="text-orange-400" />
          </div>
        </div>
      </div>

      <div className="card-lhs p-5 sm:p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-4">Activer une clé de licence</h2>
        <form onSubmit={activate} className="flex flex-col sm:flex-row gap-3 sm:max-w-2xl">
          <input
            data-testid="license-key-input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            placeholder="LHS-XXXX-XXXX-XXXX-XXXX"
            className="input-lhs font-mono-tech uppercase flex-1"
          />
          <button data-testid="license-activate-btn" disabled={loading} className="btn-orange px-5 py-3 sm:py-2 rounded-lg text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Activer
          </button>
        </form>
        {msg && (
          <div data-testid="license-msg" className={`mt-3 text-sm px-3 py-2 rounded-lg border ${
            msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                   : "text-red-400 border-red-500/40 bg-red-500/10"
          }`}>{msg.text}</div>
        )}
      </div>

      <OrderSection user={user} />
    </div>
  );
}

function StatMini({ icon: Icon, label, value, color = "text-white" }) {
  return (
    <div className="card-lhs px-2 sm:px-4 py-3">
      <div className="flex items-center gap-1 sm:gap-2 justify-center text-[9px] sm:text-[10px] uppercase text-zinc-500 tracking-wider font-mono-tech">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display font-extrabold text-base sm:text-lg mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function OrderSection({ user }) {
  const [mode, setMode] = useState(null);
  const [mine, setMine] = useState([]);
  const loadMine = () => axios.get(`${API}/license-orders/mine`).then(r => setMine(r.data)).catch(() => {});
  useEffect(() => { loadMine(); }, []);
  return (
    <div className="card-lhs p-5 sm:p-6 space-y-5" data-testid="licences-order-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-sm">Acheter une licence</h2>
          <p className="text-xs text-zinc-400 mt-1">Choisissez votre formule · activation par l'administrateur après vérification du paiement.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setMode("buy")} data-testid="licences-buy-btn" className="btn-orange rounded-lg px-5 py-2.5 text-sm uppercase tracking-wider">
            Acheter une licence
          </button>
          <button onClick={() => setMode("other")} data-testid="licences-order-btn" className="rounded-lg px-4 py-2.5 text-sm border border-zinc-700 hover:border-orange-500 transition-colors">
            Autre logiciel
          </button>
        </div>
      </div>
      {mode === "buy" && <LicensePurchase onDone={loadMine} />}
      {mode === "other" && <LicenseOrderForm defaults={{ username: user?.name, email: user?.email }} onSent={loadMine} />}
      {mine.length > 0 && (
        <div className="space-y-2" data-testid="my-orders">
          <div className="text-[11px] uppercase text-zinc-500 font-mono-tech">Mes demandes</div>
          {mine.map(o => <MyOrder key={o.id} o={o} />)}
        </div>
      )}
    </div>
  );
}

function MyOrder({ o }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-xs space-y-2" data-testid={`my-order-${o.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="font-mono-tech text-orange-400">{o.reference}</span>
        <span className="text-zinc-300">{o.software}{o.plan ? ` · ${o.plan}` : ""}{o.amount != null ? ` · ${o.amount} ${o.currency}` : ""}</span>
        <span className="text-zinc-500">{fmtLocal(o.created_at)}</span>
        <OrderStatusPill s={o.status} />
        <button onClick={() => setOpen(!open)} data-testid={`my-order-history-${o.id}`} className="text-zinc-500 hover:text-orange-400">Historique</button>
      </div>
      {o.reject_reason && <div className="text-red-400">Motif du rejet : {o.reject_reason}</div>}
      {o.expires_at && <div className="text-zinc-400">Expire le {fmtLocal(o.expires_at)}</div>}
      {open && <OrderHistory items={o.history} />}
    </div>
  );
}
